import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  Upload
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ClearOutlined,
  UnlockOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import systemService from '../../services/systemService';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const RateLimitSettings = () => {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [stats, setStats] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();

  // Fetch rules
  const fetchRules = async () => {
    setLoading(true);
    try {
      const data = await systemService.getRateLimitRules();
      if (data.success) {
        setRules(data.data?.rules || []);
        setEnabled(data.data?.enabled ?? true);
      }
    } catch (error) {
      message.error('获取规则失败: ' + (error?.error || error?.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const data = await systemService.getRateLimitStats();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('获取统计失败:', error);
    }
  };

  // Fetch blocked list
  const fetchBlocked = async () => {
    try {
      const data = await systemService.getRateLimitBlocked();
      if (data.success) {
        setBlocked(data.data || []);
      }
    } catch (error) {
      console.error('获取封禁列表失败:', error);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchStats();
  }, []);

  // Toggle rate limiting
  const handleToggle = async (checked) => {
    try {
      await systemService.toggleRateLimit(checked);
      setEnabled(checked);
      message.success(checked ? '速率限制已启用' : '速率限制已禁用');
    } catch (error) {
      message.error('操作失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle create/update rule
  const handleSubmit = async (values) => {
    try {
      // Transform form values to API format
      const rule = {
        scenario: values.scenario,
        pattern: values.pattern,
        enabled: values.enabled ?? true,
        methods: values.methods || ['*'],
        key_type: values.key_type,
        limit_guest: values.limit_guest,
        limit_user: values.limit_user,
        window: values.window,
        skip_anonymous: values.skip_anonymous ?? false,
        captcha_threshold: values.captcha_threshold ? values.captcha_threshold / 100 : 0.8,
        require_captcha: values.require_captcha ?? true,
        captcha_type: values.captcha_type || 'slider',
        captcha_title: values.captcha_title || 'Security Verification',
        captcha_message: values.captcha_message || 'Please complete verification to continue',
        description: values.description
      };

      await systemService.saveRateLimitRule(rule);
      message.success(editingRule ? '规则更新成功' : '规则创建成功');
      setModalVisible(false);
      form.resetFields();
      setEditingRule(null);
      fetchRules();
    } catch (error) {
      message.error('操作失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle delete rule
  const handleDelete = async (scenario) => {
    try {
      await systemService.deleteRateLimitRule(scenario);
      message.success('规则删除成功');
      fetchRules();
    } catch (error) {
      message.error('删除失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle unblock
  const handleUnblock = async (key) => {
    try {
      await systemService.unblockRateLimit(key);
      message.success('解封成功');
      fetchBlocked();
    } catch (error) {
      message.error('解封失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle clear cache
  const handleClearCache = async () => {
    try {
      const data = await systemService.clearRateLimitCache();
      message.success(data?.message || '缓存已清除');
      fetchStats();
      fetchBlocked();
    } catch (error) {
      message.error('清除失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle init default rules
  const handleInitRules = async () => {
    try {
      await systemService.initRateLimitRules();
      message.success('默认规则已初始化');
      fetchRules();
    } catch (error) {
      message.error('初始化失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const data = await systemService.exportRateLimitRules();
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rate_limit_rules.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('规则导出成功');
    } catch (error) {
      message.error('导出失败: ' + (error?.error || error?.message || '未知错误'));
    }
  };

  // Handle import
  const handleImport = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        await systemService.importRateLimitRules(e.target.result);
        message.success('规则导入成功');
        fetchRules();
      } catch (error) {
        message.error('导入失败: ' + (error?.error || error?.message || '未知错误'));
      }
    };
    reader.readAsText(file);
    return false;
  };

  // Open modal for creating/editing
  const openModal = (rule = null) => {
    setEditingRule(rule);
    if (rule) {
      form.setFieldsValue({
        ...rule,
        captcha_threshold: rule.captcha_threshold ? rule.captcha_threshold * 100 : 80
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        enabled: true,
        limit_guest: 60,
        limit_user: 120,
        window: 60,
        key_type: 'ip',
        skip_anonymous: false,
        captcha_threshold: 80,
        require_captcha: true,
        captcha_type: 'slider',
        captcha_title: '',
        captcha_message: '',
        methods: ['*']
      });
    }
    setModalVisible(true);
  };

  // Open blocked modal
  const openBlockedModal = () => {
    fetchBlocked();
    setBlockedModalVisible(true);
  };

  const columns = [
    {
      title: '场景',
      dataIndex: 'scenario',
      key: 'scenario',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {record.description && (
            <Tooltip title={record.description}>
              <InfoCircleOutlined style={{ color: '#999' }} />
            </Tooltip>
          )}
        </Space>
      )
    },
    {
      title: '路由模式',
      dataIndex: 'pattern',
      key: 'pattern',
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: '请求限制',
      key: 'limit',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text>游客: {record.limit_guest === 0 ? <Tooltip title="0 = 每次请求都要先过验证码"><Tag color="orange">每次验证码</Tag></Tooltip> : `${record.limit_guest} 次`} / {record.window}s</Text>
          <Text>用户: {record.limit_user === 0 ? <Tooltip title="0 = 每次请求都要先过验证码"><Tag color="orange">每次验证码</Tag></Tooltip> : `${record.limit_user} 次`} / {record.window}s</Text>
        </Space>
      )
    },
    {
      title: 'Key类型',
      dataIndex: 'key_type',
      key: 'key_type',
      render: (type) => {
        const colors = { ip: 'blue', uid: 'green', 'ip_uid': 'purple' };
        const labels = { ip: 'IP', uid: '用户ID', 'ip_uid': 'IP + UID' };
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
      }
    },
    {
      title: '验证码',
      key: 'captcha',
      render: (_, record) => (
        <Space>
          {record.require_captcha ? (
            <>
              <Tag color="orange">阈值: {Math.round((record.captcha_threshold || 0.8) * 100)}%</Tag>
              <Tag color="cyan">{record.captcha_type || 'slider'}</Tag>
            </>
          ) : (
            <Tag>禁用</Tag>
          )}
        </Space>
      )
    },
    {
      title: '方法',
      dataIndex: 'methods',
      key: 'methods',
      render: (methods) => {
        // Convert string to array if needed
        const methodList = Array.isArray(methods) ? methods : (methods || '*').split(',');
        return (
          <Space>
            {methodList.map(m => (
              <Tag key={m}>{m.trim()}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此规则吗?"
            onConfirm={() => handleDelete(record.scenario)}
            okText="是"
            cancelText="否"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const blockedColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: '场景',
      dataIndex: 'scenario',
      key: 'scenario',
    },
    {
      title: '请求数',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: '过期时间',
      dataIndex: 'expires',
      key: 'expires',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          icon={<UnlockOutlined />}
          onClick={() => handleUnblock(record.key)}
        >
          解封
        </Button>
      )
    }
  ];

  return (
    <div>
      <Title level={3}>
        <ThunderboltOutlined style={{ marginRight: 8 }} />
        速率限制
      </Title>


      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card>
            <Statistic
              title={<Tooltip title="关掉后所有接口限流和登录验证码都停用"><span>总开关 <Tag color="red">谨慎</Tag></span></Tooltip>}
              value={enabled ? '已启用' : '已禁用'}
              valueStyle={{ color: enabled ? '#3f8600' : '#cf1322' }}
              prefix={enabled ? <SafetyOutlined /> : <WarningOutlined />}
            />
            <Switch
              checked={enabled}
              onChange={handleToggle}
              checkedChildren="启用"
              unCheckedChildren="禁用"
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="规则总数"
              value={rules.length}
              prefix={<SafetyOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="启用规则"
              value={rules.filter(r => r.enabled).length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="总请求数"
              value={stats?.total_requests || 0}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="被限制请求"
              value={stats?.blocked_requests || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="速率限制规则"
        extra={
          <Space>
            <Button icon={<WarningOutlined />} onClick={openBlockedModal}>
              封禁列表
            </Button>
            <Upload
              accept=".json"
              showUploadList={false}
              beforeUpload={handleImport}
            >
              <Button icon={<UploadOutlined />}>导入</Button>
            </Upload>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出
            </Button>
            <Popconfirm
              title="清除所有速率限制缓存?"
              description="这将重置所有速率限制计数器。"
              onConfirm={handleClearCache}
              okText="是"
              cancelText="否"
            >
              <Button icon={<ClearOutlined />} danger>
                清除缓存
              </Button>
            </Popconfirm>
            <Popconfirm
              title="初始化默认规则?"
              description="这将添加一套默认的速率限制规则。"
              onConfirm={handleInitRules}
              okText="是"
              cancelText="否"
            >
              <Button icon={<PlayCircleOutlined />}>
                初始化默认
              </Button>
            </Popconfirm>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { fetchRules(); fetchStats(); }}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              添加规则
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="scenario"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingRule ? '编辑速率限制规则' : '创建速率限制规则'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRule(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="scenario"
                label="场景名称"
                rules={[{ required: true, message: '请输入场景名称' }]}
                extra="唯一标识符，如 api_general, auth_login"
              >
                <Input placeholder="e.g., api_general" disabled={!!editingRule} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="pattern"
                label="路由模式"
                rules={[{ required: true, message: '请输入路由模式' }]}
                extra="支持正则或通配符，如 /api/*, /api/auth/*"
              >
                <Input placeholder="e.g., /api/*" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={2} placeholder="可选的规则描述" />
          </Form.Item>

          <Divider>速率限制设置</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="limit_guest"
                label="游客限制 (次/窗口)"
                tooltip="0 = 每次请求都要先过验证码"
                rules={[{ required: true, message: '必填' }]}
              >
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="limit_user"
                label="用户限制 (次/窗口)"
                tooltip="0 = 每次请求都要先过验证码"
                rules={[{ required: true, message: '必填' }]}
              >
                <InputNumber min={0} max={10000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="window"
                label="窗口 (秒)"
                rules={[{ required: true, message: '必填' }]}
              >
                <InputNumber min={1} max={86400} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="key_type"
                label="Key类型"
                rules={[{ required: true, message: '必填' }]}
              >
                <Select>
                  <Option value="ip">IP地址</Option>
                  <Option value="uid">用户ID</Option>
                  <Option value="ip_uid">IP + 用户ID</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="methods"
                label="HTTP方法"
                extra="留空表示全部方法"
              >
                <Select mode="multiple" placeholder="全部方法">
                  <Option value="*">全部 (*)</Option>
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="DELETE">DELETE</Option>
                  <Option value="PATCH">PATCH</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="skip_anonymous"
                label="跳过匿名用户"
                valuePropName="checked"
                extra="未登录时不限制"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="enabled"
                label="启用"
                valuePropName="checked"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>验证码设置</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="require_captcha"
                label="需要验证码"
                valuePropName="checked"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="captcha_threshold"
                label="验证码阈值 (%)"
                extra="达到限制的该百分比时触发验证码"
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="captcha_type"
                label="验证码类型"
              >
                <Select>
                  <Option value="slider">滑块验证</Option>
                  <Option value="click">点击验证</Option>
                  <Option value="simple">简单计算</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="captcha_title"
                label="验证码弹窗标题"
              >
                <Input placeholder="如: 安全验证" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="captcha_message"
                label="验证码提示消息"
              >
                <Input placeholder="如: 请完成人机验证后继续" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRule ? '更新规则' : '创建规则'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingRule(null);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Blocked List Modal */}
      <Modal
        title="封禁列表"
        open={blockedModalVisible}
        onCancel={() => setBlockedModalVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={blockedColumns}
          dataSource={blocked}
          rowKey="key"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default RateLimitSettings;
